import type {ChatEventNode} from 'llm/chat-library/messages/MessageTypes';

type AcknowledgementChatEventNode = Extract<ChatEventNode, {__typename: 'AcknowledgementChatEventNode'}>;
type AcknowledgedInterruptResultEvent = Extract<
  NonNullable<AcknowledgementChatEventNode['resultEvent']>,
  {__typename: 'InterruptChatEventNode'}
>;

/** Whether an interrupt request has been acknowledged with a completed interrupt result. */
export function hasAcknowledgedInterruptForRequest(
  events: ReadonlyArray<ChatEventNode>,
  interruptRequestId: string
): boolean {
  return getInterruptResultEventForRequest(events, interruptRequestId) != null;
}

/**
 * Whether any interrupt request after the given display event has been acknowledged with a result.
 * Scans backwards from the end of the event list until the display event is reached.
 */
export function hasAcknowledgedInterruptEvent(
  events: ReadonlyArray<ChatEventNode>,
  forDisplayEventId: string
): boolean {
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    if (event.__typename === 'InterruptRequestChatEventNode') {
      if (getInterruptResultEventForRequest(events, event.id) != null) {
        return true;
      }
    }
    if (event.__typename === 'DisplayMessageChatEventNode' && event.id === forDisplayEventId) {
      break;
    }
  }
  return false;
}

/** Interrupt result event for a request, resolved via acknowledgement. */
export function getInterruptResultEventForRequest(
  events: ReadonlyArray<ChatEventNode>,
  interruptRequestId: string
): AcknowledgedInterruptResultEvent | null {
  for (const event of events) {
    if (event.__typename !== 'AcknowledgementChatEventNode') {
      continue;
    }
    if (event.acknowledgedEvent.__typename !== 'InterruptRequestChatEventNode') {
      continue;
    }
    if (event.acknowledgedEvent.id !== interruptRequestId) {
      continue;
    }
    const resultEvent = event.resultEvent;
    if (resultEvent?.__typename !== 'InterruptChatEventNode') {
      return null;
    }
    return resultEvent;
  }
  return null;
}
